/**
 * Semantic Action Mapper
 * Uses TensorFlow.js Universal Sentence Encoder to map natural language to Redux actions
 */

// Import types only, not implementations to avoid loading TensorFlow at startup
import type * as use from '@tensorflow-models/universal-sentence-encoder';
import type * as tf from '@tensorflow/tfjs';

// Dynamic imports will be done lazily when needed
let tfModule: typeof tf | null = null;
let useModule: typeof use | null = null;

// Lazy load TensorFlow modules
async function loadTensorFlowModules() {
	if (!tfModule || !useModule) {
		[tfModule, useModule] = await Promise.all([
			import('@tensorflow/tfjs'),
			import('@tensorflow-models/universal-sentence-encoder')
		]);
	}
	return { tf: tfModule, use: useModule };
}

// Configuration constants
export const CONFIDENCE_THRESHOLD = 0.7;
export const MAX_ACTIONS_PER_RESPONSE = 5;
export const MODEL_CACHE_TIMEOUT = 3600000; // 1 hour in ms

// Action template interface
export interface ActionTemplate {
	intent: string;
	phrases: string[];
	reduxAction: (data?: any) => any;
	actionSequence?: Array<(data?: any) => any>; // Support for action sequences
	category: 'navigation' | 'data' | 'ui' | 'modal';
	requiresConfirmation: boolean;
	destructive: boolean;
	requiresData?: boolean;
	schema?: object;
	schemaConfig?: {
		buttonLabel?: string;
		columns?: number;
		staged?: boolean;
		hasTitle?: boolean;
	};
	description?: string;
	feedbackScore?: number; // User feedback multiplier for this action
}

// Matched action interface
export interface MatchedAction {
	intent: string;
	confidence: number;
	originalConfidence: number; // TensorFlow confidence before feedback adjustment
	feedbackMultiplier: number; // Applied feedback multiplier
	template: ActionTemplate;
	sourcePage?: string; // Screen/page where the action was detected (e.g., 'EasyModeScreen', 'DataScreen', 'CalendarScreen')
	sourceContext?: Record<string, any>; // Additional context about where the action came from
}

// Semantic Action Mapper Class
class SemanticActionMapper {
	private model: any | null = null; // Type will be use.UniversalSentenceEncoder once loaded
	private modelLoadPromise: Promise<void> | null = null;
	private actionTemplates: Map<string, ActionTemplate> = new Map();
	private templateEmbeddings: Map<string, any> = new Map(); // Type will be tf.Tensor2D once loaded
	private isInitialized: boolean = false;
	private modelLoadTime: number = 0;
	private feedbackMultipliers: Map<string, number> = new Map(); // Cache for feedback multipliers
	private userId: string | null = null;

	/**
	 * Initialize the Universal Sentence Encoder model
	 */
	async initializeModel(): Promise<void> {
		// Return existing promise if already loading
		if (this.modelLoadPromise) {
			return this.modelLoadPromise;
		}

		// Return immediately if already initialized
		if (this.isInitialized && this.model) {
			// Check if cache is still valid
			if (Date.now() - this.modelLoadTime < MODEL_CACHE_TIMEOUT) {
				return Promise.resolve();
			}
		}

		// Start loading
		this.modelLoadPromise = (async () => {
			try {
				// Load TensorFlow modules dynamically
				const { use } = await loadTensorFlowModules();

				// Try to load the QnA model (same as TutorialModal.native.tsx)
				try {
					this.model = await use.loadQnA();
				} catch (qnaError) {
					try {
						this.model = await use.load();
					} catch (useError) {
						// Fallback: Use simple keyword matching instead of embeddings
						this.isInitialized = true;
						this.modelLoadTime = Date.now();
						this.model = null; // Will trigger keyword matching
						return;
					}
				}

				this.isInitialized = true;
				this.modelLoadTime = Date.now();

				// Wait a bit to ensure model internal state is fully ready (especially on web)
				await new Promise(resolve => setTimeout(resolve, 100));

				// Test the model with a simple embed call to verify it's ready
				try {
					await this.model.embed(['test']);
				} catch (testError: any) {
					// If test fails, model isn't ready yet - wait a bit more and retry once
					if (testError?.message?.includes('map') || testError?.message?.includes('undefined')) {
						console.warn('⚠️ Model not fully ready, waiting a bit longer...');
						await new Promise(resolve => setTimeout(resolve, 200));
					}
				}

				// Pre-compute embeddings for all registered templates
				await this.preComputeTemplateEmbeddings();
			} catch (error) {
				console.error(
					'❌ Failed to initialize semantic mapper:',
					error
				);
				// Don't throw - use fallback mode
				this.isInitialized = true;
				this.model = null;
				console.log('✅ Using fallback keyword matching mode');
			} finally {
				this.modelLoadPromise = null;
			}
		})();

		return this.modelLoadPromise;
	}

	/**
	 * Register an action template
	 */
	registerActionTemplate(template: ActionTemplate): void {
		if (
			!template.intent ||
			!template.phrases ||
			template.phrases.length === 0
		) {
			console.warn('Invalid action template:', template);
			return;
		}

		this.actionTemplates.set(template.intent, template);
	}

	/**
	 * Register multiple action templates
	 */
	registerActionTemplates(templates: ActionTemplate[]): void {
		templates.forEach((template) => this.registerActionTemplate(template));
	}

	/**
	 * Pre-compute embeddings for all registered action templates
	 */
	private async preComputeTemplateEmbeddings(): Promise<void> {
		if (!this.model) {
			throw new Error('Model not initialized');
		}

		console.log('🔄 Pre-computing template embeddings...');

		// Collect all phrases for batch embedding
		const allPhrases: string[] = [];
		const intentMap: Map<number, string> = new Map();
		let phraseIndex = 0;

		for (const [intent, template] of this.actionTemplates.entries()) {
			// Validate template has phrases array
			if (
				!template ||
				!template.phrases ||
				!Array.isArray(template.phrases) ||
				template.phrases.length === 0
			) {
				console.warn(
					`⚠️ Template "${intent}" has invalid or empty phrases array, skipping embedding computation`
				);
				continue;
			}

			// Combine all phrases for this template
			const combinedPhrase = template.phrases.join('. ');
			if (combinedPhrase.trim()) {
				allPhrases.push(combinedPhrase);
				intentMap.set(phraseIndex, intent);
				phraseIndex++;
			}
		}

		// Early return if no valid phrases to embed
		if (allPhrases.length === 0) {
			console.warn('⚠️ No valid phrases found for embedding computation');
			return;
		}

		try {
			// Validate model and phrases before embedding
			if (!this.model) {
				console.warn(
					'⚠️ Model not initialized for embedding computation'
				);
				return;
			}

			if (!this.model.embed || typeof this.model.embed !== 'function') {
				console.warn('⚠️ Model embed method not available');
				return;
			}

			if (!allPhrases || allPhrases.length === 0) {
				console.warn('⚠️ No phrases to embed');
				return;
			}

			// Ensure all phrases are strings and non-empty (double-check)
			const validPhrases = allPhrases.filter(
				(phrase) =>
					typeof phrase === 'string' && phrase.trim().length > 0
			);
			if (validPhrases.length === 0) {
				console.warn('⚠️ No valid phrases to embed after filtering');
				return;
			}

			// Rebuild intentMap to match validPhrases indices
			const validIntentMap = new Map<number, string>();
			let validIndex = 0;
			for (let i = 0; i < allPhrases.length; i++) {
				if (
					typeof allPhrases[i] === 'string' &&
					allPhrases[i].trim().length > 0
				) {
					const originalIntent = intentMap.get(i);
					if (originalIntent) {
						validIntentMap.set(validIndex, originalIntent);
						validIndex++;
					}
				}
			}

			// Use the embed method (works for both QnA and full USE models)
			// Wrap in try-catch to handle TensorFlow internal errors
			let embeddings;
			try {
				// Ensure validPhrases is a valid array with strings
				if (!Array.isArray(validPhrases) || validPhrases.length === 0) {
					console.warn('⚠️ Invalid phrases array for embedding');
					return;
				}

				// Double-check all phrases are strings
				const finalPhrases = validPhrases.filter(
					(p) => typeof p === 'string' && p.trim().length > 0
				);
				if (finalPhrases.length === 0) {
					console.warn(
						'⚠️ No valid string phrases after final filter'
					);
					return;
				}

				embeddings = await this.model.embed(finalPhrases);

				// Validate embeddings result
				if (!embeddings) {
					console.warn('⚠️ Model.embed() returned null/undefined');
					return;
				}
			} catch (embedError: any) {
				// Check if error is about undefined/map (model not ready)
				if (
					embedError?.message?.includes('map') ||
					embedError?.message?.includes('undefined') ||
					embedError?.message?.includes('Cannot read properties')
				) {
					// This is a known issue - model internal state not ready yet
					// Suppress error log since we handle it gracefully with fallback
					console.warn(
						'⚠️ Model internal state not ready yet. Will use keyword matching fallback.'
					);
				} else {
					// Other errors should still be logged
					console.error(
						'⚠️ Error calling model.embed():',
						embedError?.message || embedError
					);
				}
				return;
			}

			// Split embeddings for each template using validPhrases indices
			for (let i = 0; i < validPhrases.length; i++) {
				const intent = validIntentMap.get(i);
				if (intent && embeddings) {
					try {
						// Extract single embedding for this template
						const singleEmbedding = embeddings.slice(
							[i, 0],
							[1, -1]
						) as tf.Tensor2D;
						this.templateEmbeddings.set(intent, singleEmbedding);
					} catch (sliceError) {
						console.warn(
							`⚠️ Failed to extract embedding for template "${intent}":`,
							sliceError
						);
					}
				}
			}

			console.log(
				`✅ Computed embeddings for ${this.templateEmbeddings.size} templates`
			);
		} catch (error) {
			console.error('Failed to compute template embeddings:', error);
		}
	}

	/**
	 * Compute cosine similarity between two tensors
	 */
	private cosineSimilarity(a: any, b: any): number {
		if (!tfModule) {
			throw new Error('TensorFlow not loaded');
		}
		return tfModule.tidy(() => {
			const dotProduct = tfModule.matMul(a, b, false, true);
			const normA = tfModule.norm(a, 'euclidean', 1, true);
			const normB = tfModule.norm(b, 'euclidean', 1, true);
			const similarity = tfModule.div(
				dotProduct,
				tfModule.mul(normA, normB)
			);
			return similarity.dataSync()[0];
		});
	}

	/**
	 * Fallback keyword-based matching when model is unavailable
	 */
	private keywordBasedMatching(
		text: string,
		sourcePage?: string,
		sourceContext?: Record<string, any>
	): MatchedAction[] {
		const lowerText = text.toLowerCase();
		const textWords = lowerText.split(/\s+/).filter((w) => w.length > 0);
		const matches: MatchedAction[] = [];

		// Keyword matching mode (fallback)

		for (const [intent, template] of this.actionTemplates.entries()) {
			let maxScore = 0;
			let bestMatchingWords = 0;
			let shortestPhrase = Infinity;

			// Check each phrase for keyword matches
			for (const phrase of template.phrases) {
				const phraseWords = phrase
					.toLowerCase()
					.split(/\s+/)
					.filter((w) => w.length > 2);

				// Track shortest phrase length
				if (phraseWords.length < shortestPhrase) {
					shortestPhrase = phraseWords.length;
				}

				// Count exact word matches (not just substring includes)
				const matchingWords = phraseWords.filter((word) =>
					textWords.some(
						(textWord) =>
							textWord === word || textWord.includes(word)
					)
				);

				// Calculate score with weighting
				// - Base score: percentage of phrase words found
				// - Bonus: if phrase appears as exact substring (higher confidence)
				const baseScore =
					phraseWords.length > 0
						? matchingWords.length / phraseWords.length
						: 0;
				const exactMatch = lowerText.includes(phrase.toLowerCase())
					? 0.2
					: 0;
				const score = Math.min(baseScore + exactMatch, 1.0);

				if (score > maxScore) {
					maxScore = score;
					bestMatchingWords = matchingWords.length;
				}
			}

			// Only add if score is above threshold
			// Dynamic requirement: 1 word for short phrases (1-2 words), 2 for longer
			const minWords = shortestPhrase <= 2 ? 1 : 2;

			const passedThreshold = maxScore >= CONFIDENCE_THRESHOLD;
			const passedMinWords = bestMatchingWords >= minWords;

			if (passedThreshold && passedMinWords) {
				// Apply feedback multiplier to confidence
				const feedbackResult = this.applyFeedbackToConfidence(
					maxScore,
					intent
				);

				matches.push({
					intent,
					confidence: feedbackResult.adjustedConfidence,
					originalConfidence: maxScore,
					feedbackMultiplier: feedbackResult.multiplier,
					template,
					sourcePage,
					sourceContext,
				});
			}
		}

		// Sort by confidence and limit
		matches.sort((a, b) => b.confidence - a.confidence);
		return matches.slice(0, MAX_ACTIONS_PER_RESPONSE);
	}

	/**
	 * Analyze a text response and return matched actions
	 * @param text - The text to analyze for actionable intents
	 * @param sourcePage - Optional: The screen/page where this analysis is happening (e.g., 'EasyModeScreen', 'DataScreen')
	 * @param sourceContext - Optional: Additional context about the source (e.g., { dataId: '123', screen: 'DataItem' })
	 */
	async analyzeResponse(
		text: string,
		sourcePage?: string,
		sourceContext?: Record<string, any>
	): Promise<MatchedAction[]> {
		if (!this.isInitialized) {
			console.warn('Mapper not initialized, skipping analysis');
			return [];
		}

		if (!text || text.trim().length === 0) {
			return [];
		}

		// Use fallback keyword matching if model is not available
		if (!this.model) {
			console.log('🔄 Using keyword-based matching (fallback mode)');
			return this.keywordBasedMatching(text, sourcePage, sourceContext);
		}

		try {
			// Encode the input text using USE model
			// Add validation to ensure model is ready
			if (!this.model || typeof this.model.embed !== 'function') {
				throw new Error('Model not ready');
			}

			const queryEmbedding = await this.model.embed([text.toLowerCase()]);
			
			// Validate embedding result
			if (!queryEmbedding) {
				throw new Error('Embedding returned null/undefined');
			}
			
			const queryTensor = queryEmbedding as tf.Tensor2D;

			const matches: MatchedAction[] = [];

			// Compare with all template embeddings
			for (const [
				intent,
				templateEmbedding,
			] of this.templateEmbeddings.entries()) {
				const similarity = this.cosineSimilarity(
					queryTensor,
					templateEmbedding
				);

				// Only include matches above threshold
				if (similarity >= CONFIDENCE_THRESHOLD) {
					const template = this.actionTemplates.get(intent);
					if (template) {
						// Apply feedback multiplier to confidence
						const feedbackResult = this.applyFeedbackToConfidence(
							similarity,
							intent
						);

						matches.push({
							intent,
							confidence: feedbackResult.adjustedConfidence,
							originalConfidence: similarity,
							feedbackMultiplier: feedbackResult.multiplier,
							template,
							sourcePage,
							sourceContext,
						});
					}
				}
			}

			// Cleanup tensors
			queryEmbedding.dispose();

			// Sort by confidence (highest first) and limit to MAX_ACTIONS
			matches.sort((a, b) => b.confidence - a.confidence);
			const topMatches = matches.slice(0, MAX_ACTIONS_PER_RESPONSE);

			if (topMatches.length > 0) {
				console.log(
					'🎯 Matched actions (semantic):',
					topMatches.map((m) => ({
						intent: m.intent,
						confidence: (m.confidence * 100).toFixed(1) + '%',
					}))
				);
			}

			return topMatches;
		} catch (error: any) {
			// Check if this is the known "map" error - suppress noisy error log
			if (
				error?.message?.includes('map') ||
				error?.message?.includes('undefined') ||
				error?.message?.includes('Cannot read properties')
			) {
				// Model not ready - silently fall back to keyword matching
				console.log('🔄 Model not ready, using keyword matching');
			} else {
				console.error(
					'Error in semantic analysis, falling back to keywords:',
					error
				);
			}
			// Fallback to keyword matching on error
			return this.keywordBasedMatching(text, sourcePage, sourceContext);
		}
	}

	/**
	 * Get all registered templates
	 */
	getRegisteredTemplates(): ActionTemplate[] {
		return Array.from(this.actionTemplates.values());
	}

	/**
	 * Check if model is ready
	 */
	isReady(): boolean {
		return this.isInitialized && this.model !== null;
	}

	/**
	 * Clear all cached data
	 */
	clear(): void {
		// Dispose all embeddings
		for (const embedding of this.templateEmbeddings.values()) {
			embedding.dispose();
		}

		this.templateEmbeddings.clear();
		this.actionTemplates.clear();
		this.isInitialized = false;
		this.model = null;
		this.modelLoadTime = 0;
		this.feedbackMultipliers.clear();
		this.userId = null;
	}

	/**
	 * Set user ID for personalized feedback
	 */
	setUserId(userId: string): void {
		this.userId = userId;
	}

	/**
	 * Load feedback multipliers for actions
	 */
	async loadFeedbackMultipliers(): Promise<void> {
		if (!this.userId) return;

		try {
			// Import here to avoid circular dependencies
			const { getCachedFeedbackMultiplier } =
				await import('./actionFeedbackManager');

			// Preload feedback data for all registered action intents
			const actionIntents = Array.from(this.actionTemplates.keys());

			for (const intent of actionIntents) {
				const multiplier = getCachedFeedbackMultiplier(
					intent,
					this.userId
				);
				this.feedbackMultipliers.set(intent, multiplier);
			}
		} catch (error) {
			console.error('Error loading feedback multipliers:', error);
		}
	}

	/**
	 * Get feedback multiplier for an action intent
	 */
	getFeedbackMultiplier(actionIntent: string): number {
		return this.feedbackMultipliers.get(actionIntent) || 1.0;
	}

	/**
	 * Apply feedback to confidence score
	 */
	applyFeedbackToConfidence(
		confidence: number,
		actionIntent: string
	): { adjustedConfidence: number; multiplier: number } {
		const multiplier = this.getFeedbackMultiplier(actionIntent);
		const adjustedConfidence = Math.max(
			0.1,
			Math.min(1.0, confidence * multiplier)
		);

		return {
			adjustedConfidence,
			multiplier,
		};
	}

	/**
	 * Update feedback multiplier for an action
	 */
	updateFeedbackMultiplier(actionIntent: string, multiplier: number): void {
		this.feedbackMultipliers.set(actionIntent, multiplier);
	}

	/**
	 * Clear feedback multipliers cache
	 */
	clearFeedbackCache(): void {
		this.feedbackMultipliers.clear();
	}
}

// Export singleton instance
export const semanticActionMapper = new SemanticActionMapper();

// Export helper functions
export const initializeSemanticMapper = () =>
	semanticActionMapper.initializeModel();
export const registerActionTemplate = (template: ActionTemplate) =>
	semanticActionMapper.registerActionTemplate(template);
export const registerActionTemplates = (templates: ActionTemplate[]) =>
	semanticActionMapper.registerActionTemplates(templates);
export const analyzeResponse = (
	text: string,
	sourcePage?: string,
	sourceContext?: Record<string, any>
) => semanticActionMapper.analyzeResponse(text, sourcePage, sourceContext);
export const isMapperReady = () => semanticActionMapper.isReady();
export const getRegisteredTemplates = () =>
	semanticActionMapper.getRegisteredTemplates();

// Export feedback-related functions
export const setUserId = (userId: string) =>
	semanticActionMapper.setUserId(userId);
export const loadFeedbackMultipliers = () =>
	semanticActionMapper.loadFeedbackMultipliers();
export const updateFeedbackMultiplier = (
	actionIntent: string,
	multiplier: number
) => semanticActionMapper.updateFeedbackMultiplier(actionIntent, multiplier);
export const clearFeedbackCache = () =>
	semanticActionMapper.clearFeedbackCache();
